import React from 'react';
import { useAppSelector } from 'src/redux/hooks';
import { AppState } from 'src/redux/store';
import { selectModuleDataByIds, determineSectionProgressIcon } from 'src/redux/slices/learningModules';
import { ContentProgressStatus } from 'src/types/contentProgress';
import { LearningModuleStateData } from 'src/types/state/learningModule';
import { LearningPathwaySection } from 'src/types/state/learningPathway';
import {
  ModuleSectionAccordionItemV2,
  ModuleSectionBodyItemV2,
  WorkflowTrackerStepStateV2,
} from '@amzn/seller-university-ui-components';
import { t, resolveContent } from 'src/utils/translation';
import { BUNDLE_NAME, LEARNING_PATHWAY_STRING_ID, CREDENTIAL_STRING_ID } from 'src/constants/localization';
import { useBundle } from '@amzn/react-arb-tools';
import { timeConverter } from 'src/utils/timeFormat';
import { REF_TAG } from 'src/constants/refTag';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';

/**
 * Assessment appended to the end of a credential section's module list.
 * Locked (greyed, non-interactive) until all modules complete; unlocked shows a CTA
 * that opens the assessment.
 */
export interface CredentialAssessment {
  title: string;
  description: string;
  imageSrc: string;
  ctaLabel: string;
  questionCount: number;
  locked: boolean;
  earned: boolean;
  /** True when the seller has an in-flight (started but not yet passed) assessment attempt. */
  inProgress: boolean;
  onStart: () => void;
}

interface ModuleSectionItemV2Props {
  section: LearningPathwaySection;
  index: number;
  onModuleSelect: (moduleId: string) => void;
  expanded?: boolean;
  isLocalized?: boolean;
  hideSectionLabel?: boolean;
  assessment?: CredentialAssessment;
  /** When true, modules and the assessment at or after the serial-lock frontier are gated. */
  enforceSerialProgression?: boolean;
  /** The global index (across all sections) of this section's first module. */
  sectionModuleStartIndex?: number;
  /** Global index of the first not-yet-complete module; items after it are gated. */
  serialLockFrontierIndex?: number;
}

const ModuleSectionItemV2: React.FC<ModuleSectionItemV2Props> = ({
  section,
  index,
  onModuleSelect,
  expanded = false,
  isLocalized = false,
  hideSectionLabel = false,
  assessment,
  enforceSerialProgression = false,
  sectionModuleStartIndex = 0,
  serialLockFrontierIndex = 0,
}) => {
  const moduleDataMap = useAppSelector(
    (state: AppState) => selectModuleDataByIds(state, section.learningModuleIds),
  );

  const sectionProgressIcon = useAppSelector(
    (state: AppState) => determineSectionProgressIcon(state, section.learningModuleIds),
  );

  const [learningPathwayBundle] = useBundle(BUNDLE_NAME.LEARNING_PATHWAY);
  const [credentialBundle] = useBundle(BUNDLE_NAME.CREDENTIAL);

  // Credential content carries `credential-`-prefixed string IDs that live in the credential
  // bundle and are never pre-localized, so they must always resolve against it. Other pathways
  // keep resolving section content against the learning-pathway bundle, honoring isLocalized
  // for content that may already be localized (PolyD).
  const resolveSectionContent = (content: string) => (
    content.startsWith('credential-')
      ? t(credentialBundle, content)
      : resolveContent(learningPathwayBundle, content, isLocalized)
  );

  const mapProgressStatus = (status: ContentProgressStatus): WorkflowTrackerStepStateV2 => {
    switch (status) {
      case ContentProgressStatus.COMPLETED:
        return 'complete';
      case ContentProgressStatus.IN_PROGRESS:
        return 'in-progress';
      case ContentProgressStatus.NOT_STARTED:
      default:
        return 'todo';
    }
  };

  const buildStatusPill = (state: WorkflowTrackerStepStateV2) => {
    const STATUS_PILL_STRING_ID: Record<WorkflowTrackerStepStateV2, string> = {
      complete: CREDENTIAL_STRING_ID.STATUS_COMPLETED,
      'in-progress': CREDENTIAL_STRING_ID.STATUS_IN_PROGRESS,
      todo: CREDENTIAL_STRING_ID.STATUS_NOT_STARTED,
    };
    return { status: state, label: t(credentialBundle, STATUS_PILL_STRING_ID[state]) };
  };

  const buildLockedPill = (tooltipStringId: string) => ({
    status: 'neutral' as const,
    label: t(credentialBundle, CREDENTIAL_STRING_ID.STATUS_LOCKED),
    icon: 'lock' as const,
    tooltip: t(credentialBundle, tooltipStringId),
  });

  const mapInstanceTypeToContentVariant = (instanceType?: string) => {
    switch (instanceType?.toUpperCase()) {
      case 'VIDEO':
        return 'Video' as const;
      case 'DOCUMENT':
      case undefined:
      default:
        return 'Pdf' as const;
    }
  };

  const getDefaultInstance = (module: LearningModuleStateData) => module.instanceMap?.[module.defaultLanguage];

  const getModuleMetadata = (module: LearningModuleStateData): string[] => {
    const defaultInstance = getDefaultInstance(module);

    if (!defaultInstance) {
      return [];
    }

    if (defaultInstance.instanceType?.toUpperCase() === 'VIDEO' && defaultInstance.duration) {
      return [timeConverter(defaultInstance.duration)];
    }
    if (defaultInstance.instanceType?.toUpperCase() === 'DOCUMENT' && defaultInstance.pageSize) {
      return [t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_PDF_PAGES,
        {
          count: defaultInstance.pageSize,
        },
      )];
    }
    return [];
  };

  const { clickMetric } = useMetrics();

  const handleModuleSectionBoxClick = (moduleId: string) => {
    clickMetric({
      feature: FeatureName.LearningPathwaySection,
      metadata: {
        sectionId: section.sectionId,
        sectionTitle: section.title,
        moduleId,
      },
    });
    onModuleSelect(moduleId);
  };

  /* istanbul ignore next */
  const handleModuleSectionCtaClick = () => {
    clickMetric({
      feature: FeatureName.LearningPathwaySectionCta,
      metadata: {
        sectionId: section.sectionId,
        sectionTitle: section.title,
        ctaText: section.ctaText,
        ctaLink: section.ctaLink,
      },
    });
  };

  const moduleBodyItems: ModuleSectionBodyItemV2[] = section.learningModuleIds
    .map((moduleId, moduleIndexInSection): ModuleSectionBodyItemV2 | null => {
      const module = moduleDataMap[moduleId];
      // A section can reference a module not yet in the store (unloaded, or inserted with
      // undefined data by search/autocomplete matchers); skip it rather than dereference undefined.
      if (!module) {
        return null;
      }
      const moduleVariant = getDefaultInstance(module)
        ? mapInstanceTypeToContentVariant(getDefaultInstance(module)!.instanceType)
        : 'Pdf' as const;
      const count = getModuleMetadata(module).join(', ');
      const moduleState = mapProgressStatus(module.moduleProgressStatus);
      const globalModuleIndex = sectionModuleStartIndex + moduleIndexInSection;
      const serialLocked = enforceSerialProgression && globalModuleIndex > serialLockFrontierIndex;
      return {
        progress: {
          state: serialLocked ? 'todo' : moduleState,
          id: module.moduleId,
        },
        statusPill: serialLocked
          ? buildLockedPill(CREDENTIAL_STRING_ID.MODULE_LOCKED_TOOLTIP)
          : buildStatusPill(moduleState),
        locked: serialLocked,
        marker: {
          variant: moduleVariant,
          label: t(
            learningPathwayBundle,
            moduleVariant === 'Video'
              ? LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_MARKER_VIDEO
              : LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_MARKER_PDF,
          ),
          count,
        },
        content: {
          id: module.moduleId,
          title: module.title,
          metadata: count,
          moduleVariant,
          previewDescription: module.description,
          imageSrc: module.thumbnailLink,
          onClick: () => handleModuleSectionBoxClick(module.moduleId),
        },
      };
    })
    .filter((item): item is ModuleSectionBodyItemV2 => item !== null);

  const moduleCount = moduleBodyItems.length;

  if (assessment) {
    let assessmentState: WorkflowTrackerStepStateV2 = 'todo';
    if (assessment.locked) {
      assessmentState = 'todo';
    } else if (assessment.earned) {
      assessmentState = 'complete';
    } else if (assessment.inProgress) {
      assessmentState = 'in-progress';
    }
    moduleBodyItems.push({
      progress: {
        state: assessmentState,
        id: 'assessment',
      },
      locked: assessment.locked,
      statusPill: assessment.locked
        ? buildLockedPill(CREDENTIAL_STRING_ID.ASSESSMENT_LOCKED_TOOLTIP)
        : buildStatusPill(assessmentState),
      cta: assessment.locked || assessment.earned
        ? undefined
        : { label: assessment.ctaLabel, onClick: assessment.onStart },
      marker: {
        variant: 'Assessment',
        label: t(learningPathwayBundle, LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_MARKER_ASSESSMENT),
        count: t(
          learningPathwayBundle,
          LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_ASSESSMENT_QUESTIONS,
          { count: assessment.questionCount },
        ),
      },
      content: {
        id: 'assessment',
        title: assessment.title,
        metadata: '',
        moduleVariant: 'Pdf' as const,
        previewDescription: assessment.description,
        imageSrc: assessment.imageSrc,
      },
    });
  }

  return (
    <ModuleSectionAccordionItemV2
      moduleSectionLabel={hideSectionLabel ? undefined : t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_SECTION_LABEL,
        { number: index + 1 },
      )}
      moduleSectionTitle={resolveSectionContent(section.title)}
      moduleSectionDescription={resolveSectionContent(section.description)}
      progressIcon={sectionProgressIcon}
      totalModulesLabel={t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_TOTAL_MODULES_LABEL,
        {
          count: moduleCount,
        },
      )}
      ModuleSectionBodyItems={moduleBodyItems}
      expanded={expanded}
      ctaButton={section.ctaLink && section.ctaText
        ? {
          label: resolveSectionContent(section.ctaText),
          href: section.ctaLink,
          refTag: REF_TAG.LEARNING_PATHWAY_SECTION_CTA_BTN,
          onClick: handleModuleSectionCtaClick,
        }
        : undefined}
    />
  );
};

export default ModuleSectionItemV2;
